import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Vital Leopard private drop (sections/leopard-drop.liquid) — OPEN state only.
 * This file is not even loaded in the TEASER state, so the teaser can never
 * add anything to a cart.
 *
 * It does exactly three things:
 *   1. size chip → remembers which variant id the shopper picked on that tile
 *      (the ids are Shopify's own, server-rendered from product.variants —
 *      nothing is looked up, hardcoded or computed here);
 *   2. "Add to bag" → ONE ordinary `/cart/add.js` call for that variant, no
 *      properties, no discount, no attributes, then the same
 *      CartLinesUpdateEvent + `detail.sections` refresh convention already
 *      proven in cart-socks-promo.js / brand-rep-welcome-gift.js, which the
 *      theme's own cart drawer / cart icon listen for (drawer opens via the
 *      theme's existing `auto_open_cart_drawer` behaviour);
 *   3. optional tracking through Shopify's own custom-event publisher.
 *
 * ANALYTICS: no pixel, no dataLayer, no second analytics system. Add-to-cart,
 * checkout and purchase are already captured by Shopify's standard events
 * because this uses the same cart endpoint as every other add-to-cart path, so
 * nothing here re-fires them. The only extra signals are three small custom
 * events (`leopard_shop_cta_click`, `leopard_product_click`,
 * `leopard_size_selected`) sent via `Shopify.analytics.publish` — a no-op
 * unless a Customer Events pixel subscribes to them. UTM parameters are left
 * untouched: Shopify records the landing URL (with its UTMs) on the visit and
 * the order, so links are never rewritten.
 *
 * DUPLICATE PROTECTION: a module-scoped `addInFlight` flag ignores taps while a
 * request is pending, and the button is disabled for the duration.
 */

const FEEDBACK_RESET_MS = 1600;

let addInFlight = false;

function publish(name, payload) {
  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (error) {
    /* tracking must never affect shopping */
  }
}

function tileContext(tile) {
  return {
    productId: tile?.dataset.productId || null,
    productHandle: tile?.dataset.productHandle || null,
  };
}

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

function setMessage(tile, text) {
  const el = tile.querySelector('[data-leopard-message]');
  if (el) el.textContent = text || '';
}

function setAddLabel(button, key) {
  const label = button.dataset[key];
  if (label) button.textContent = label;
}

function selectSize(tile, chip) {
  tile.querySelectorAll('[data-leopard-size]').forEach((other) => {
    other.setAttribute('aria-checked', other === chip ? 'true' : 'false');
  });
  tile.dataset.selectedVariant = chip.dataset.variantId;

  const add = tile.querySelector('[data-leopard-add]');
  if (add) {
    add.removeAttribute('aria-disabled');
    setAddLabel(add, 'labelAdd');
  }
  setMessage(tile, '');
  publish('leopard_size_selected', { ...tileContext(tile), variantId: chip.dataset.variantId });
}

async function addToCart(tile, button) {
  const variantId = tile.dataset.selectedVariant;

  if (!variantId) {
    setMessage(tile, 'Please choose a size first.');
    tile.querySelector('[data-leopard-size]:not(:disabled)')?.focus();
    return;
  }
  if (addInFlight) return;
  addInFlight = true;
  button.disabled = true;
  setAddLabel(button, 'labelAdding');
  setMessage(tile, '');

  try {
    const body = JSON.stringify({
      // Deliberately NO `properties` — an ordinary merchandise line.
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: cartSectionIds().join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const data = await response.json();

    if (!response.ok || data.status) {
      const message = (data && (data.description || data.message)) || 'Sorry, that size could not be added. Please try again.';
      setMessage(tile, message);
      document.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      button.disabled = false;
      setAddLabel(button, 'labelAdd');
      addInFlight = false;
      return;
    }

    const freshCart = await fetchCart();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(freshCart),
          detail: {
            sections: data.sections,
            items: freshCart.items,
            itemCount: freshCart.item_count,
            source: 'leopard-drop',
            didError: false,
          },
        }),
      })
    );

    setAddLabel(button, 'labelAdded');
    window.setTimeout(() => {
      if (!button.isConnected) return;
      button.disabled = false;
      setAddLabel(button, 'labelAdd');
    }, FEEDBACK_RESET_MS);
    addInFlight = false;
  } catch (error) {
    console.error('[leopard-drop] add failed', error);
    setMessage(tile, 'Sorry, something went wrong. Please try again.');
    button.disabled = false;
    setAddLabel(button, 'labelAdd');
    addInFlight = false;
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const root = target.closest('[data-leopard-drop]');
  if (!root) return;

  const cta = target.closest('[data-leopard-shop-cta]');
  if (cta) {
    publish('leopard_shop_cta_click', {});
    const destination = document.getElementById('leopard-drop-products');
    if (destination) {
      event.preventDefault();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      destination.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
    return;
  }

  const productLink = target.closest('[data-leopard-product-link]');
  if (productLink) {
    publish('leopard_product_click', tileContext(productLink.closest('[data-leopard-tile]')));
    return;
  }

  const chip = target.closest('[data-leopard-size]');
  if (chip instanceof HTMLButtonElement && !chip.disabled) {
    const tile = chip.closest('[data-leopard-tile]');
    if (tile instanceof HTMLElement) selectSize(tile, chip);
    return;
  }

  const add = target.closest('[data-leopard-add]');
  if (add instanceof HTMLButtonElement && !add.disabled) {
    const tile = add.closest('[data-leopard-tile]');
    if (tile instanceof HTMLElement) {
      event.preventDefault();
      addToCart(tile, add);
    }
  }
});
